import { useEffect, useRef, type MutableRefObject } from 'react';
import type { FieldPath, UseFormReturn } from 'react-hook-form';

import { TripLegType } from '@/data/models/reservation.model';
import reservationUtils from '@/services/reservationUtils';
import { useClientMemberContact } from '@/hooks/useClientMemberContact';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { usePortalLabel } from '@/hooks/usePortalLabel';

import type {
  ReservationFormInput,
  ReservationFormValues,
} from '../schema/reservationForm.schema';
import type { BundledValidationOutput } from '../machines/runBundledValidation';
import {
  isLowConfidenceError,
  tripLegConfidenceErrorTranslationKey,
} from '../machines/tripLegConfidence';
import {
  triggerReservationPhoneFieldValidation,
  type ReservationPhoneFieldValidationOptions,
} from '../utils/triggerReservationPhoneFieldValidation';

type ReservationForm = UseFormReturn<
  ReservationFormInput,
  unknown,
  ReservationFormValues
>;

/** Paths from `getReservationFormFieldErrors`, e.g. `trips[1].pickupDateTime`. */
function tripLegIndexFromFormFieldPath(field: string): number | undefined {
  const m = /^trips\[(\d+)\]\./.exec(field);
  if (m == null) return undefined;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Shared applier that maps each bundled-validation result onto a reservation
 * form. Returns the ref to hand to `ReservationFormMachinesProvider`'s
 * `applyTripLegValidationResultsToForm` — the machine calls it after every
 * `/score/trip-leg` + `/validate` round. Used by CreateReservationDialog,
 * AddTripLegDialog, and EditTripLegDialog.
 *
 * Per round it:
 *  - applies backend timezones from `validations.tripLegUpdates`;
 *  - diff-clears only the server-error paths applied on the prior round,
 *    leaving RHF/Zod errors intact (mirrors `prevValidationFieldsRef` in the
 *    legacy admin-portal `home/reservations/ReservationDialog.tsx`);
 *  - maps `/validate` errors (via `getReservationFormFieldErrors`, round-trip
 *    flip included; client portal's low address-confidence errors are filtered
 *    out here and rendered as yellow field warnings instead) and `/score`
 *    trip-leg errors (via their score-time `formFieldPath`) onto the form,
 *    trip-leg errors last so they win on a shared field;
 *  - collapses `GenericError` messages into the `genericErrors` field;
 *  - when all server errors clear, re-runs the resolver on the freed paths so
 *    Zod errors they were masking re-surface — skipping freed fields the user
 *    has since emptied (their required error waits for submit, like any
 *    untouched empty field);
 *  - re-runs client-side phone validation, with per-dialog options resolved at
 *    call time via `getPhoneRevalidationOptions` (e.g. excluding member-form
 *    phone fields while the member step/tab isn't visible).
 *
 * `clientBC` (the passenger's client) and `servicePlanID` (the passenger's
 * service plan — the contact is plan-scoped) feed the client member-contact
 * interpolation (`{{clientName}}`/`{{clientNumber}}`) in the translated
 * messages — see `useClientMemberContact` for the fetch and fallbacks.
 */
export function useTripLegValidationFormApplier(
  form: ReservationForm,
  getPhoneRevalidationOptions?: () =>
    | ReservationPhoneFieldValidationOptions
    | undefined,
  clientBC?: string | null,
  servicePlanID?: string | null,
): MutableRefObject<(output: BundledValidationOutput) => void> {
  const t = useCommonTranslation();
  const isClientPortal = usePortalLabel() === 'client-portal';
  const clientMemberContact = useClientMemberContact(clientBC, servicePlanID);

  const applyRef = useRef<(output: BundledValidationOutput) => void>(() => {});
  /** Field paths set as server errors on the prior round (diff-clear target). */
  const appliedServerErrorPathsRef = useRef<FieldPath<ReservationFormInput>[]>(
    [],
  );
  // Late-bound so callers can pass an inline closure without churning the
  // effect below.
  const getPhoneRevalidationOptionsRef = useRef(getPhoneRevalidationOptions);
  getPhoneRevalidationOptionsRef.current = getPhoneRevalidationOptions;

  useEffect(() => {
    applyRef.current = (output: BundledValidationOutput) => {
      const { validations, errorsByTripIndex } = output;
      // Update the form values returned from validation results (timezones).
      for (const update of validations.tripLegUpdates) {
        const idx = update.tripLegSequence - 1;
        if (update.pickUpAddress?.timeZone) {
          if (form.getValues('trips')[idx] == null) continue;
          if (
            form.getValues('trips')[idx].pickupTimeZone !==
            update.pickUpAddress.timeZone
          ) {
            form.setValue(
              `trips.${idx}.pickupTimeZone`,
              update.pickUpAddress.timeZone,
              { shouldValidate: true },
            );
          }
        }
        if (update.dropOffAddress?.timeZone) {
          if (form.getValues('trips')[idx] == null) continue;
          if (
            form.getValues('trips')[idx].dropoffTimeZone !==
            update.dropOffAddress.timeZone
          ) {
            form.setValue(
              `trips.${idx}.dropoffTimeZone`,
              update.dropOffAddress.timeZone,
              { shouldValidate: true },
            );
          }
        }
      }
      const priorAppliedPaths = appliedServerErrorPathsRef.current;
      if (priorAppliedPaths.length > 0) {
        form.clearErrors(priorAppliedPaths);
      }
      const formTripType = form.getValues('tripType');
      const redValidateErrors = isClientPortal
        ? validations.errors.filter((e) => !isLowConfidenceError(e))
        : validations.errors;
      const validateMessages =
        redValidateErrors.length > 0
          ? reservationUtils.getReservationFormFieldErrors(
              t,
              formTripType === TripLegType.RoundTrip,
              { ...validations, errors: redValidateErrors },
              clientMemberContact,
            )
          : [];

      const tripLegMessages: { field: string; message: string }[] = [];
      for (const err of Object.values(errorsByTripIndex).flat()) {
        // Target field is resolved once at score time
        if (!err.formFieldPath) continue;
        const translationKey = tripLegConfidenceErrorTranslationKey(err);
        const message =
          (translationKey
            ? (t(translationKey, {
                ...(err.errorCodeData ?? {}),
                ...clientMemberContact,
                defaultValue: err.message ?? '',
              }) as string)
            : err.message) || '';
        if (!message) continue;
        tripLegMessages.push({ field: err.formFieldPath, message });
      }

      const messages = [...validateMessages, ...tripLegMessages];

      const nextAppliedPaths: FieldPath<ReservationFormInput>[] = [];
      if (messages.length > 0) {
        const tripsSnapshot = form.getValues('trips');
        const genericErrors: string[] = [];

        for (const message of messages) {
          if (message.field === 'GenericError') {
            genericErrors.push(message.message);
            continue;
          }

          const legIndex = tripLegIndexFromFormFieldPath(message.field);
          if (legIndex != null && tripsSnapshot[legIndex] == null) {
            continue;
          }

          const fieldPath = message.field as FieldPath<ReservationFormInput>;
          if (form.getValues(fieldPath) == null) continue;

          form.setError(fieldPath, {
            type: 'manual',
            message: message.message,
          });
          nextAppliedPaths.push(fieldPath);
        }

        if (genericErrors.length > 0) {
          form.setError('genericErrors', {
            type: 'manual',
            message: Array.from(new Set(genericErrors)).join('\n'),
          });
          nextAppliedPaths.push(
            'genericErrors' as FieldPath<ReservationFormInput>,
          );
        }
      } else if (priorAppliedPaths.length > 0) {
        // Re-run the resolver on the freed paths so any client-side (Zod)
        // errors they were masking get re-surfaced. Skip freed fields the user
        // has since emptied (e.g. a cleared address): like any untouched empty
        // field, their required error should wait for submit, not fire on
        // clear.
        const pathsToRevalidate = priorAppliedPaths.filter((path) => {
          const value = form.getValues(path);
          return !(
            value == null ||
            (typeof value === 'string' && !value.trim())
          );
        });
        if (pathsToRevalidate.length > 0) {
          form.trigger(pathsToRevalidate, { shouldFocus: false });
        }
      }
      appliedServerErrorPathsRef.current = nextAppliedPaths;
      triggerReservationPhoneFieldValidation(
        form,
        getPhoneRevalidationOptionsRef.current?.(),
      );
    };
  }, [form, isClientPortal, t, clientMemberContact]);

  return applyRef;
}
